import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";
import AdminLayout from "../../layout/AdminLayout";
import { useAuth } from "../../../context/AuthContext";
import * as model from "../../../models/InterviewsModel";
import { useNotificationCount } from "../../../context/NotificationCountContext";
import InterviewTable from "./InterviewTable";
import { ConfirmDelete } from "../../modals/ConfirmDelete";
import { apiError } from "../../../helpers/ErrorHelper";
import { toast } from "react-toastify";
import PageHeader from "../../common/PageHeader";
import ListFilterActions from "../../common/ListFilterActions";
import config from "../../../config/config";
import { checkPermission, formatAMPM } from "../../../helpers/GeneralHelper";
import { getData as getClients } from '../../../models/ClientsModel';
import { getData as getRequirements } from '../../../models/RequirementsModel';
import { getData as getInterviewStatus } from '../../../models/InterviewStatusModel';
import { MyReactSelect } from "../../inputs/inputs";
import { useDebouncedCallback } from 'use-debounce';
import { useCallback } from "react";
import dateFormat from "dateformat";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getRecruiters } from "../../../models/UserModel";
import PipelineFilterBar from "../../common/PipelineFilterBar";
import { usePipelineUrlFilters } from "../../../hooks/usePipelineUrlFilters";

export default function Interviews() {
  const module = {
    title: 'Interviews',
    singularTitle: 'Interview',
    key: 'interviews',
    route: 'interviews',
    primaryCol: '_interview_id'
  };

  const { state } = useAuth();
  let user_data = state.user_data;
  const [searchParams] = useSearchParams();
  var _client_id = searchParams.get('_client_id');
  var _requirement_id = searchParams.get('_requirement_id');
  var _candidate_id = searchParams.get('_candidate_id');
  const navigate = useNavigate();

  const addInterview = useCallback(() => {
    const prefill = {
      candidateId: searchParams.get("_candidate_id"),
      requirementId: searchParams.get("_requirement_id"),
      clientId: searchParams.get("_client_id"),
    };
    const hasPrefill = Object.values(prefill).some(Boolean);
    navigate(`/interviews/create`, {
      state: {
        module,
        ...(hasPrefill ? { prefill } : {}),
      },
    });
  }, [module, navigate, searchParams]);

  const { notificationCount, setNotificationCount } = useNotificationCount();
  const [modalKey, setModalKey] = useState(0);
  const [toggleAddModal, setToggleAddModal] = useState(false);

  // Update Start
  const [toggleUpdateModal, setToggleUpdateModal] = useState(false);
  const [singleData, setSingleData] = useState({});
  const openUpdateModal = (data) => {
    navigate(`/interviews/${data.interview_unique_id}/edit`, { 
      state: {
        data: data,
        module: module,
      },
    });
    console.log('Navigating to edit interview');
  };
  // Update End

  // Delete Start
  const [toggleDeleteModal, setToggleDeleteModal] = useState(false);
  const openDeleteModal = (data) => {
    setSingleData(data);
    setToggleDeleteModal(true);
  };

  const deleteData = async() => {
    setLoading(true);

    try {
      const response = await model.remove(user_data.user_token, singleData[module.primaryCol]);
      if (response.status) {
        setNotificationCount(localStorage.getItem('unread_notification_count'));
        toast.success(response.message);

        model.saveToStorage(response.data[module.key], response.data.pagination);
        refreshData(); 
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);

      if (error.response?.data.invalid_token == 1) {
        navigate('/re-login');
      }
    }

    setToggleDeleteModal(false);
    setLoading(false);
  }
  // Delete End

  const [toggleViewModal, setToggleViewModal] = useState(false);
  const handleView = (data) => {
    navigate(`/interviews/${data.interview_unique_id}/view`, { 
      state: {
        data: data,
        module: module,
      },
    });
  }

  // Flush Start
  const flashData = async () => {
    localStorage.removeItem(module.key);
    fetchData(limit, page);
  };
  // Flush End

  // Filter Start
  const [filters, setFilters] = useState({
    interview_client_id: "",
    interview_requirement_id: "",
    interview_status_id: "",
    interview_recruiter_id: "",
  });

  const [clients, setClients] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [interStatus, setInterStatus] = useState([]);
  const [recruiters, setRecruiters] = useState([]);

  useEffect(() => {
    Promise.all([
      getClients({ user_token: user_data.user_token, page: 1, limit: -1 }),
      getInterviewStatus({ user_token: user_data.user_token, page: 1, limit: -1 }),
      getRecruiters({ user_token: user_data.user_token, page: 1, limit: -1 }),
    ]).then(([clientsRes, statusRes, recruitersRes]) => {
      if (clientsRes?.clients) {
        setClients(clientsRes.clients.map(c => ({ value: c._client_id, label: c.client_name })));
      }
      if (statusRes?.interview_status) {
        setInterStatus(statusRes.interview_status.map(s => ({ value: s._status_id, label: s.status_name })));
      }
      if (recruitersRes?.recruiters) {
        setRecruiters(
          recruitersRes.recruiters.map((item) => ({
            value: item._user_id,
            label: `${item.user_first_name} ${item.user_last_name}`,
          }))
        );
      }
    }).catch(console.error);
  }, []);

  const { chips, clearUrlContext } = usePipelineUrlFilters({
    user_token: user_data.user_token,
    searchParams,
    navigate,
    routePath: "/interviews",
    clientColumn: "interview_client_id",
    requirementColumn: "interview_requirement_id",
    setFilters,
    clients,
  });

  useEffect(() => {
    const fetchRequirements = async() => {
      if(filters.interview_client_id){
        _client_id = filters.interview_client_id.value;
      }
      if (! _client_id) {
        filters.interview_requirement_id = '';
        setRequirements([]);
        return;
      }

      try {
        let apiParam = {user_token: user_data.user_token, 'requirement_client_id': _client_id, page: 1, limit: -1};
        const response = await getRequirements(apiParam);

        if (typeof response != typeof undefined) {
          let options = [];
          let flag = false;
          response.requirements.forEach(function(item) {
            if (
              filters.interview_requirement_id == item._requirement_id ||
              filters.interview_requirement_id?.value == item._requirement_id
            ) {
              flag = true;
            }
            options.push({
              value: item._requirement_id,
              label: item.requirement_name
            });
          });

          if (! flag) {
            filters.interview_requirement_id = '';
          }

          setRequirements(options);
        }
      } catch (error) {
        console.error(error);
      }
    }

    fetchRequirements();
  }, [filters.interview_client_id]);

  const [isFilterReset, setIsFilterReset] = useState(false);
  const [isTrash, setIsTrash] = useState(false);
  useEffect(() => {
    setIsFilterReset(false);
    Object.keys(filters).forEach(item => {
      if (filters[item]) {
        setIsFilterReset(true);
        return false;
      }
    });

    fetchData(10, 1);
  }, [filters, isTrash]);

  const resetFilter = () => {
    let blankFilter = {};
    Object.keys(filters).forEach(item => {
      blankFilter[item] = '';
    });
    setFilters(blankFilter);
  }
  // Filter End

  // List Start
  const [listData, setListData] = useState([]);
  const [cacheTime, setCacheTime] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchTrash = (isTrash) => {
    setIsTrash(isTrash);
  }

  //const debounced = useDebouncedCallback(() => fetchData(limit, page), 500);
  const debounced = useDebouncedCallback((value) => {
    setSearchQuery(value);
    fetchData(limit, page, value);  // pass value directly
  }, 500);


  const fetchData = async (newLimit, page) => {
    setLoading(true);
    const urlClientId = searchParams.get("_client_id");
    const urlRequirementId = searchParams.get("_requirement_id");
    const urlCandidateId = searchParams.get("_candidate_id");
    if(filters.interview_client_id){
      _client_id = filters.interview_client_id.value;
    } else if (urlClientId) {
      _client_id = urlClientId;
    }
    if(filters.interview_requirement_id){
      _requirement_id = filters.interview_requirement_id.value;
    } else if (urlRequirementId) {
      _requirement_id = urlRequirementId;
    }
    if (urlCandidateId) {
      _candidate_id = urlCandidateId;
    }
    try {
      let apiParam = {
        search: searchQuery,
        page: page,
        limit: newLimit,
        user_token: user_data.user_token,
        interview_client_id: _client_id ? _client_id : '',
        interview_requirement_id: _requirement_id ? _requirement_id : '',
        interview_candidate_id: _candidate_id ? _candidate_id : '',
        interview_status_id: filters?.interview_status_id?.value ?? '',
        interview_recruiter_id: filters?.interview_recruiter_id?.value ?? '',
        isTrash: isTrash ? 1 : 0,
      };
      const response = await model.getData(apiParam);
      if (typeof response == typeof undefined) {
        navigate("/re-login");
        return;
      }

      setListData(response[module.key]);
      setTotalRows(response.pagination.total_rows);
      setLimit(newLimit);
      setPage(page);

      setNotificationCount(localStorage.getItem("unread_notification_count"));

      if (response.time) {
        setCacheTime(response.time);
      }

      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };
  // List End

  const refreshData = async () => {
    fetchData(limit, page);
  };
  const exportToExcel = async () => {
    if (!listData || listData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    // Generate timestamp (YYYYMMDD_HHMMSS + random 4 digits)
    const timestamp = new Date().toISOString()
                                .replace(/[-:T]/g, "") // remove special chars
                                .split(".")[0];        // remove milliseconds

    const fileName = `Interviews_${timestamp}.xlsx`;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Interviews");

    // Define headers and style
    const headers = [
      { header: "Client", key: "client_name" },
      { header: "Requirement", key: "requirement_name" },
      { header: "Candidate", key: "candidate_name" },
      { header: "Scheduled By", key: "recruiter_name" },
      { header: "Date", key: "interview_date" },
      { header: "Status", key: "status_name" },
    ];

    //worksheet.columns = headers;
    worksheet.columns = [
      { header: "Client", key: "client_name" },
      { header: "Requirement", key: "requirement_name" },
      { header: "Candidate", key: "candidate_name" },
      { header: "Scheduled By", key: "recruiter_name" },
      { header: "Date", key: "interview_date" },
      { header: "Status", key: "status_name" },
    ];

    // Add rows
    listData.forEach(row => {
      worksheet.addRow({
        client_name: row.client_name,
        requirement_name: row.requirement_name || "N/A",
        candidate_name: row.candidate_name || "N/A",
        recruiter_name: row.recruiter_name || "N/A",
        interview_date: dateFormat(row.interview_date, 'dd/mm/yyyy') + ' ' + formatAMPM(new Date('2024-04-28 ' + row.interview_time)),
        status_name: row.status_name || "N/A",
      });
    });

    // Style header
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'D9E1F2' }, // Light blue
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Generate Excel file and save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
  };
  return (
    <AdminLayout>
      <Helmet>
        <title>{module.title} - Digital CMS</title>
      </Helmet>
      <div className="main-content app-content">
        <div className="container-fluid cms-list-shell">
          <PageHeader module={module} isTrash={isTrash} toggleTrash={fetchTrash} />

          {!isTrash && (
            <PipelineFilterBar
              chips={chips}
              onClearAll={clearUrlContext}
              workflowStage="interviews"
              compactHelp
            />
          )}

          {/* <!-- Start::row-1 --> */}
          <div className="row">
            <div className="col-xl-12">
              <div className="card custom-card cms-list-card">
                {! isTrash && <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div className="card-title fw-normal" style={{textTransform: 'unset'}}>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <MyReactSelect column="interview_client_id" options={clients} firstOption="Select client" fields={filters} setFields={setFilters} /> &nbsp;&nbsp;
                      <MyReactSelect column="interview_requirement_id" options={requirements} firstOption="Select Requirement" fields={filters} setFields={setFilters} /> &nbsp;&nbsp;
                      <MyReactSelect column="interview_status_id" options={interStatus} firstOption="Select Status" fields={filters} setFields={setFilters} /> &nbsp;&nbsp;
                      <MyReactSelect column="interview_recruiter_id" options={recruiters} firstOption="Select Recruiter" fields={filters} setFields={setFilters} /> &nbsp;&nbsp;
                    <ListFilterActions
                        showExport={listData.length > 0}
                        onExport={exportToExcel}
                        showReset={isFilterReset}
                        onReset={resetFilter}
                      />

                      {/* { config.isCache ? `Last Synced On ${cacheTime}` : module.title } */}
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <div>
                      <input type="text" className="form-control table-search" onChange={(e) => {debounced(e.target.value); setSearchQuery(e.target.value);}} placeholder="Search..." autoComplete='off' />

                      {checkPermission(module.route, 'add') && 
                        <button className="btn btn-primary btn-sm" onClick={addInterview}>
                          <i className="ri-add-line me-1 fw-semibold align-middle"></i>
                          Add
                        </button>
                      }
                    </div>

                    {config.isCache ? 
                      <button className="btn btn-success-light btn-sm" onClick={() => flashData()}>
                        <i className="ri-refresh-line me-1 fw-semibold align-middle"></i> Refresh
                      </button> : ''
                    }
                  </div>
                </div>}
                <div className="card-body">
                  <div className="table-responsive">
                    <InterviewTable
                      module={module}
                      data={listData}
                      loading={loading}
                      totalRows={totalRows}
                      page={page}
                      limit={limit}
                      fetchData={fetchData}
                      openUpdateModal={openUpdateModal}
                      openDeleteModal={openDeleteModal}
                      handleView={handleView}
                      isTrash={isTrash}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* <!--End::row-1 --> */}

          {/* <!--Delete Modal Start--> */}
          <ConfirmDelete
            module={module}
            showModal={toggleDeleteModal}
            setShowModal={setToggleDeleteModal}
            loading={loading}
            onConfirm={deleteData}
          />
          {/* <!--Delete Modal End--> */}

          {/* View Modal */}
          {/* <InterviewView
            module={module}
            showModal={toggleViewModal}
            setShowModal={setToggleViewModal}
            data={singleData}
          /> */}
        </div>
      </div>
      <Helmet></Helmet>
    </AdminLayout>
  );
}